import React from 'react';
import { useRoleAwareLayout } from '../../hooks/responsive/useResponsive';
import { migrationMonitor } from '../../utils/architecture/migrationMonitor';
import { createLazyComponent, DashboardSkeleton, lazyLoadMonitor } from '../../utils/architecture/codeSplitting.jsx';
import ErrorBoundary from '../ErrorBoundary';


// Import existing components (preserve functionality)
import DashboardContent from './content/DashboardContent';
import DashboardErrorFallback from './DashboardErrorFallback';


// Optimized lazy loading with performance monitoring
const NewDashboardContent = createLazyComponent(
  () => {
    console.log('🔄 Starting to load DashboardContent...');
    lazyLoadMonitor.startLoad('DashboardContent');
    return import('./content/DashboardContent').then(module => {
      console.log('✅ DashboardContent loaded successfully');
      lazyLoadMonitor.endLoad('DashboardContent', true);
      return module;
    }).catch(error => {
      console.error('❌ Failed to load DashboardContent:', error);
      lazyLoadMonitor.endLoad('DashboardContent', false);
      throw error;
    });
  },
  () => {
    console.log('🔄 Using legacy dashboard fallback');
    return <LegacyDashboardFallback />;
  },
  {
    loadingComponent: <DashboardSkeleton />,
    preload: true // Dashboard is critical route
  }
);

const ResponsiveLayout = createLazyComponent(
  () => import('../layouts/ResponsiveLayout'),
  ({ children }) => <div className="layout-fallback">{children}</div>,
  {
    loadingComponent: <div>Loading layout...</div>,
    preload: true
  }
);

const DashboardContainer = () => {
  const { isMobile } = useRoleAwareLayout();
  
  // Performance tracking
  React.useEffect(() => {
    migrationMonitor.startPerformanceTracking('dashboard-render');
    return () => migrationMonitor.endPerformanceTracking('dashboard-render');
  }, []);
  
  // Lightweight error fallback
  const ErrorFallback = ({ error }) => (
    <DashboardErrorFallback error={error} isMobile={isMobile} />
  );
  
  // Error boundary for new architecture
  const NewArchitectureComponent = () => (
    <ErrorBoundary 
      fallback={ErrorFallback}
      onError={(error) => {
        console.error('Dashboard architecture failed:', error);
        migrationMonitor.errors.push({
          component: 'DashboardContainer',
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }}
    >
      <ResponsiveLayout>
        <DashboardContent />
      </ResponsiveLayout>
    </ErrorBoundary>
  );
  
  // Always use new architecture (feature flag permanently enabled)
  return <NewArchitectureComponent />;
};

export default DashboardContainer;